
import { Firestore, doc, getDoc } from "firebase/firestore";
import { toast } from "sonner";
import { CampLocation } from "@/types/campLocation";
import { publishLocationVersion } from "../locationVersionCreateService";
import { updateCampLocation } from "../campLocationUpdateService";
import { format } from "date-fns";
import { getCampLocation } from "../campLocationQueryService";
import { getLatestLocationVersion } from "./versionStatusService";
import { ensurePlaceholderOffersForLocation } from "../offer/placeholderOfferService";
import { isLocationEditLiveSource } from "@/config/locationEditFlag";
import { publishLocationLiveOnly } from "./publishLocationLiveService";

/**
 * Global function to publish the latest version of a location
 * 
 * @param db Firestore instance
 * @param locationId Location ID to publish
 * @param userId User ID performing the publish action
 * @param options Additional options for publishing
 * @returns Promise resolving to the published version ID
 */
export const publishLocationChanges = async (
  db: Firestore,
  locationId: string,
  userId: string,
  options?: {
    showToasts?: boolean;
    updateCallback?: (id: string) => Promise<void>;
    refreshVersionHistory?: () => void;
  }
) => {
  const { showToasts = true, updateCallback, refreshVersionHistory } = options || {};

  console.log(`[publishLocationService] Starting publish process for location ${locationId}`);

  // KB-3308: live-only publish flips visibility only — no version to require, no snapshot spread,
  // no full-doc rewrite. The placeholder-offer sync and UI-refresh effects still run as today.
  if (isLocationEditLiveSource()) {
    try {
      const publishedLocationId = await publishLocationLiveOnly(db, locationId, userId);

      // Ensure placeholder offers exist and are synced with programs (non-blocking, as on the
      // version path).
      try {
        const pl = await ensurePlaceholderOffersForLocation(db, locationId, userId);
        console.log(`[publishLocationService] Placeholder sync - created: ${pl.created}, updated: ${pl.updated}, deleted: ${pl.deleted}`);
      } catch (e) {
        console.error("[publishLocationService] Placeholder sync failed", e);
      }

      if (updateCallback) {
        console.log("[publishLocationService] Running update callback (live-only)");
        await updateCallback(locationId);
      }

      if (refreshVersionHistory) {
        refreshVersionHistory();
      } else if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("versionchange"));
        window.dispatchEvent(new CustomEvent("refreshVersionHistory"));
      }

      if (showToasts) {
        toast.success("Location published successfully");
      }

      // Truthy: the two welcome callers treat a falsy return as a failed publish.
      return publishedLocationId;
    } catch (error) {
      console.error("[publishLocationService] Error during live-only publish:", error);
      if (showToasts) {
        toast.error(`Failed to publish location: ${(error as Error).message}`);
      }
      return null;
    } finally {
      if (typeof document !== "undefined") {
        document.body.style.pointerEvents = "";
      }
    }
  }

  try {
    // Get the latest version
    const latestVersion = await getLatestLocationVersion(db, locationId);
    if (!latestVersion) {
      console.error("[publishLocationService] No draft version found to publish");
      if (showToasts) {
        toast.error("No draft version found to publish");
      }
      return null;
    }
    
    // Extract just the version ID string
    const latestVersionId = latestVersion.versionId;
    
    console.log(`[publishLocationService] Publishing version ${latestVersionId}`);
    await publishLocationVersion(db, latestVersionId, userId);
    
    console.log("[publishLocationService] Creating new draft version after publishing");
    
    // Get the updated location after publishing first
    const updatedLocation = await getCampLocation(db, locationId);
    if (!updatedLocation) {
      console.error("[publishLocationService] Failed to retrieve location after publishing");
      return null;
    }
    
    console.log("[publishLocationService] Retrieved updated location after publishing:", updatedLocation.id);
    
    // Ensure placeholder offers exist and are synced with programs
    try {
      const pl = await ensurePlaceholderOffersForLocation(db, locationId, userId);
      console.log(`[publishLocationService] Placeholder sync - created: ${pl.created}, updated: ${pl.updated}, deleted: ${pl.deleted}`);
    } catch (e) {
      console.error("[publishLocationService] Placeholder sync failed", e);
      // Non-blocking
    }

    // Generate a timestamp for the current time
    const currentTimestamp = format(new Date(), "MMM d, yyyy 'at' h:mm a");
    
    // Create a new draft version based on the published version
    await updateCampLocation(
      db, 
      locationId, 
      updatedLocation, 
      userId, 
      `Published version ${currentTimestamp}`, 
      false, 
      true
    );
    
    // Fetch the location again to get the latest version reference
    const refreshedLocation = await getCampLocation(db, locationId);
    
    if (updateCallback && refreshedLocation) {
      console.log("[publishLocationService] Running update callback");
      await updateCallback(locationId);
    }
    
    // Trigger version history refresh if callback provided
    if (refreshVersionHistory) {
      console.log("[publishLocationService] Triggering version history refresh");
      refreshVersionHistory();
    } else {
      // Dispatch events to refresh version history content
      console.log("[publishLocationService] Dispatching version change events");
      window.dispatchEvent(new CustomEvent("versionchange"));
      window.dispatchEvent(new CustomEvent("refreshVersionHistory"));
    }
    
    if (showToasts) {
      toast.success("Location published successfully");
    }
    
    return latestVersionId;
  } catch (error) {
    console.error("[publishLocationService] Error during publish process:", error);
    if (showToasts) {
      toast.error(`Failed to publish location: ${(error as Error).message}`);
    }
    return null;
  } finally {
    // Ensure we reset the document.body.style.pointerEvents
    document.body.style.pointerEvents = '';
  }
};
